import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { Button, Center, Row, Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CardSpinner, ConfirmPopup } from '~/commonComponents';
import browserHistory from '~/browserHistory';
import {
  fireBulkConfirmCandidateSelected,
  fireBulkConfirmCandidateViewed,
  fireBulkConfirmConfirmClicked,
  fireBulkConfirmNotNowClicked,
  type TBulkConfirmSelectionMethod,
} from '~/posthogs/events';
import { getCandidateOfferId } from './utils';

import { CircularLoading } from '~/images/lottie';
import { CandidateProfile } from '../candidates';
import { ShiftList } from './components';
import { useBulkConfirmation } from './hooks/useBulkConfirmation';
import { useCandidateJobs } from './hooks/useCandidateJobs';
import { useJobSelection } from './hooks/useJobSelection';
import { OutlineCheckCircleConfirmationIcon, OutlineWarning01Icon } from '@tempmee/react-icons';

export const BulkConfirmCandidateJobs = () => {
  const params = useParams<{ candidateId: string; officeId: string }>();

  const { candidate, jobs, isContractor, isEmployee, isFirstLoading } = useCandidateJobs(
    params.candidateId || '',
    params.officeId || '',
  );

  const {
    checkedJobs,
    clearCheckedJobs,
    hasSelection,
    totalCheckedJobs,
    handleToggleOption,
    isMultipleSelection,
    selectedJobIds,
  } = useJobSelection(jobs);

  const {
    confirmJobs,
    isLoadingJobsConfirmation,
    isJobsConfirmationSuccess,
    bulkConfirmationError,
    handleCloseErrorPopup,
    handleCloseSuccessPopup,
    confirmedJobIds,
  } = useBulkConfirmation({
    candidateId: params.candidateId || '',
  });

  // One impression per candidate presented — this page is scoped to a single
  // professional, so it fires once, listing the shifts they were shown against. The
  // ref keys on the candidate id (mirroring ConfirmCandidate's candidate_viewed) so
  // a re-render or a jobs refetch never double-counts (GOTU-4815).
  const viewedCandidateIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!candidate || !jobs.length) {
      return;
    }
    if (viewedCandidateIdRef.current === params.candidateId) {
      return;
    }
    viewedCandidateIdRef.current = params.candidateId ?? null;

    fireBulkConfirmCandidateViewed({
      professionalId: params.candidateId,
      candidateId: params.candidateId,
      shiftCount: jobs.length,
      shiftIds: jobs.map((job) => job.id),
      offerIds: jobs
        .map((job) => getCandidateOfferId(job, params.candidateId))
        .filter((offerId): offerId is string => Boolean(offerId)),
    });
  }, [candidate, jobs, params.candidateId]);

  const trackShiftSelected = useCallback(
    (shiftId: string, jobIndex: number, selectionMethod: TBulkConfirmSelectionMethod) => {
      fireBulkConfirmCandidateSelected({
        professionalId: params.candidateId,
        candidateId: params.candidateId,
        shiftId,
        offerId: getCandidateOfferId(jobs[jobIndex], params.candidateId),
        shiftNumber: jobIndex >= 0 ? jobIndex + 1 : undefined,
        shiftCount: jobs.length,
        selectionMethod,
      });
    },
    [jobs, params.candidateId],
  );

  const handleSelectJob = useCallback(
    (jobId?: string) => {
      if (jobId) {
        if (!checkedJobs[jobId]) {
          trackShiftSelected(
            jobId,
            jobs.findIndex((job) => job.id === jobId),
            'individual',
          );
        }
      } else if (isMultipleSelection && selectedJobIds.length !== jobs.length) {
        // "Select All" comes through with no jobId and is a toggle: it clears the
        // list once everything is checked, and only selects otherwise. Mirror the
        // single-row rule — report selections, never deselections — so this fires
        // only in the selecting direction, once per row not already checked.
        jobs.forEach((job, index) => {
          if (!checkedJobs[job.id]) {
            trackShiftSelected(job.id, index, 'select_all');
          }
        });
      }

      handleToggleOption(jobId);
    },
    [
      checkedJobs,
      handleToggleOption,
      isMultipleSelection,
      jobs,
      selectedJobIds.length,
      trackShiftSelected,
    ],
  );

  const handleConfirmJobs = useCallback(() => {
    fireBulkConfirmConfirmClicked({
      professionalId: params.candidateId,
      candidateId: params.candidateId,
      shiftCount: jobs.length,
      selectedShiftCount: selectedJobIds.length,
      shiftIds: selectedJobIds,
      offerIds: selectedJobIds
        .map((jobId) =>
          getCandidateOfferId(
            jobs.find((job) => job.id === jobId),
            params.candidateId,
          ),
        )
        .filter((offerId): offerId is string => Boolean(offerId)),
    });
    confirmJobs(jobs, selectedJobIds);
    clearCheckedJobs();
  }, [confirmJobs, jobs, selectedJobIds, clearCheckedJobs, params.candidateId]);

  if (isFirstLoading || !candidate || !jobs.length) {
    return <CardSpinner />;
  }

  return (
    <>
      <Screen variant="standard">
        <Screen.Header title="Confirm Shifts" />
        <Screen.Body>
          <Row align="center" justify="between">
            <CandidateProfile
              candidate={candidate.candidate}
              jobId={jobs[0].id}
              job={jobs[0].jobApi}
              hasWorkedWithDSO={candidate.hasWorkedWithDSO}
              hasWorkedWithOffice={candidate.hasWorkedWithOffice}
              backgroundCheckStatus={candidate?.backgroundCheckStatus}
            />
            <Row gap={3}>
              <Button
                variant="outlined"
                size="md"
                disabled={isLoadingJobsConfirmation}
                onClick={() => {
                  fireBulkConfirmNotNowClicked({
                    professionalId: params.candidateId,
                    candidateId: params.candidateId,
                    shiftCount: jobs.length,
                  });
                  browserHistory.push('/');
                }}
              >
                Not Now
              </Button>
              <Button
                disabled={!hasSelection || isLoadingJobsConfirmation || jobs.length === 0}
                size="md"
                onClick={handleConfirmJobs}
              >
                {totalCheckedJobs > 0 && isContractor ? `Confirm (${totalCheckedJobs})` : 'Confirm'}
              </Button>
            </Row>
          </Row>
          <Center px={20} style={{ marginBlock: 32 }}>
            <Typography variant="body1"
              italic
              color={colors.neutral[500]}
              style={{ textAlign: 'center' }}
            >
              The list below displays additional offers that {candidate.candidate.first_name}{' '}
              {candidate.candidate.last_name} has submitted for other shifts at your office. Review
              the candidate&apos;s application details, including any counters they submitted for
              the hourly rate and/or shift hours.
            </Typography>
          </Center>
          <ShiftList
            candidate={candidate.candidate}
            checkedJobs={checkedJobs}
            isMultipleSelection={isMultipleSelection}
            jobs={jobs}
            handleToggleOption={handleSelectJob}
          />
        </Screen.Body>
      </Screen>
      {isLoadingJobsConfirmation && (
        <ConfirmPopup
          Icon={() => (
            <Center>
              <CircularLoading />
            </Center>
          )}
          title="Processing..."
          titleStyle={{ marginTop: 36, marginBottom: 8 }}
          description="Please, wait while we confirm your selected shifts. Thank you for your patience."
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 0 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
        />
      )}
      {isJobsConfirmationSuccess && (
        <ConfirmPopup
          Icon={() => (
            <Center>
              <OutlineCheckCircleConfirmationIcon color={colors.success[400]} />
            </Center>
          )}
          title="Good News!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          description={
            isEmployee
              ? 'Your shift has successfully been filled.'
              : // eslint-disable-next-line max-len
                `${candidate.candidate.first_name} ${candidate.candidate.last_name} has been confirmed for ${confirmedJobIds.length} shift(s).`
          }
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Ok"
          rightButtonAction={handleCloseSuccessPopup}
        />
      )}

      {bulkConfirmationError && (
        <ConfirmPopup
          title="Sorry!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          Icon={() => <OutlineWarning01Icon color={colors.warning[500]} />}
          description={
            bulkConfirmationError.message ||
            'Unable to confirm any of the selected shifts. Please try again later.'
          }
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Close"
          rightButtonAction={handleCloseErrorPopup}
        />
      )}
    </>
  );
};
